// src/LoginPage.js
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include' // Important: include cookies in request
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      
      console.log('Login successful:', data);
      
      // Store user info in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Store token in localStorage for use in API requests
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        console.log('Auth token saved to localStorage');
      }
      
      // Redirect based on role
      if (data.user.role === 'therapist') {
        navigate('/therapist-dashboard'); // Therapist dashboard
      } else if (data.user.role === 'admin') {
        navigate('/admin-dashboard');
      } else {
        // Check if we have a "from" location to redirect back to
        if (location.state && location.state.from) {
          navigate(location.state.from); // Redirect to the original requested URL
        } else {
          navigate('/client-profile'); // Default to profile for clients
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 flex flex-col">
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="container mx-auto">
          <h1 className="text-xl md:text-2xl font-bold">Mindaera</h1>
        </div>
      </header>
      
      <main className="flex-grow container mx-auto py-8 px-4 flex justify-center items-center">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Log In</h2>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-md"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-md"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full p-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
            
            <div className="mt-4 text-center text-sm">
              <p>
                Don't have an account?{' '}
                <a href="/register" className="text-blue-600 hover:underline">
                  Register here
                </a>
              </p>
            </div>
          </form>
        </div>
      </main>
      
      <footer className="bg-blue-800 text-blue-200 py-4 text-center text-sm">
        <div className="container mx-auto">
          Mindaera | Internal Dev Prototype
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;